import { Fragment } from "react";
import { Link } from "wouter";
import { ChevronRight, Home } from "lucide-react";

interface BreadcrumbItem {
  label: string;
  href?: string; // If no href, it's the current page
}

interface BreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
}

export function Breadcrumb({ items, className = "" }: BreadcrumbProps) {
  return (
    <nav className={`flex items-center space-x-1 text-sm ${className}`} data-testid="breadcrumb-nav">
      <Link 
        href="/dashboard" 
        className="flex items-center text-gray-500 hover:text-purple-600 transition-colors"
        data-testid="breadcrumb-home"
      >
        <Home className="w-4 h-4" />
      </Link>
      
      {items.map((item, index) => (
        <div key={index} className="flex items-center space-x-1">
          <ChevronRight className="w-4 h-4 text-gray-400" />
          {item.href ? (
            <Link 
              href={item.href}
              className="text-gray-500 hover:text-purple-600 transition-colors font-medium"
              data-testid={`breadcrumb-item-${index}`}
            >
              {item.label}
            </Link>
          ) : (
            <span 
              className="text-purple-600 font-semibold"
              data-testid={`breadcrumb-current-${index}`}
            >
              {item.label}
            </span>
          )}
        </div>
      ))}
    </nav>
  );
}

// Hook for common breadcrumb patterns
export function useBreadcrumbs() {
  const createBreadcrumbs = {
    homes: (): BreadcrumbItem[] => [
      { label: "Care Homes", href: "/homes" }
    ],
    
    templates: (): BreadcrumbItem[] => [
      { label: "Templates", href: "/templates" }
    ],
    
    audits: (): BreadcrumbItem[] => [
      { label: "Audits", href: "/audits" }
    ],
    
    actions: (): BreadcrumbItem[] => [
      { label: "Actions", href: "/actions" }
    ],
    
    newAudit: (): BreadcrumbItem[] => [
      { label: "Audits", href: "/audits" },
      { label: "New Audit" }
    ],
    
    users: (): BreadcrumbItem[] => [
      { label: "Administration" },
      { label: "Users", href: "/admin/users" }
    ],
    
    myHome: (): BreadcrumbItem[] => [
      { label: "Administration" },
      { label: "My Home" }
    ],
    
    adminOrganizations: (): BreadcrumbItem[] => [
      { label: "Administration" },
      { label: "Organizations", href: "/admin/organizations" }
    ],
    
    homeDetail: (homeName: string): BreadcrumbItem[] => [
      { label: "Care Homes", href: "/homes" },
      { label: homeName }
    ],
    
    auditDetail: (auditName: string): BreadcrumbItem[] => [
      { label: "Audits", href: "/audits" },
      { label: auditName }
    ],
    
    custom: (items: BreadcrumbItem[]): BreadcrumbItem[] => items
  };

  return createBreadcrumbs;
}

// Responsive breadcrumb for mobile - shows only last 2 items on small screens
export function ResponsiveBreadcrumb({ items, className = "" }: BreadcrumbProps) {
  const mobileItems = items.length > 2 ? items.slice(-2) : items;
  
  return (
    <>
      {/* Desktop breadcrumb */}
      <div className="hidden sm:block">
        <Breadcrumb items={items} className={className} />
      </div>
      
      {/* Mobile breadcrumb */}
      <div className="sm:hidden">
        <nav className={`flex items-center space-x-1 text-sm ${className}`} data-testid="breadcrumb-nav-mobile">
          <Link 
            href="/dashboard" 
            className="flex items-center text-gray-500 hover:text-purple-600 transition-colors"
            data-testid="breadcrumb-home-mobile"
          >
            <Home className="w-4 h-4" />
          </Link>
          
          {items.length > 2 && (
            <>
              <ChevronRight className="w-4 h-4 text-gray-400" />
              <span className="text-gray-400 font-medium">...</span>
            </>
          )}
          
          {mobileItems.map((item, index) => (
            <div key={index} className="flex items-center space-x-1">
              <ChevronRight className="w-4 h-4 text-gray-400" />
              {item.href ? (
                <Link 
                  href={item.href}
                  className="text-gray-500 hover:text-purple-600 transition-colors font-medium"
                  data-testid={`breadcrumb-item-mobile-${index}`}
                >
                  {item.label}
                </Link>
              ) : (
                <span 
                  className="text-purple-600 font-semibold"
                  data-testid={`breadcrumb-current-mobile-${index}`}
                >
                  {item.label}
                </span>
              )}
            </div>
          ))}
        </nav>
      </div>
    </>
  );
}